import { Trash2 } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { DoclistTreeItemSectionsItem } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import { detectSimilarSectionGroups } from "~/utils/textDuplicateDetection";

interface TextAnalysisTabProps {
  sections: DoclistTreeItemSectionsItem[];
  onDeleteClick: (sections: DoclistTreeItemSectionsItem[]) => void;
  className?: string;
  onSelectGroup?: (group: DoclistTreeItemSectionsItem[]) => void;
  selectedGroup?: DoclistTreeItemSectionsItem[] | null;
  aiDetectedGroups?: DoclistTreeItemSectionsItem[][];
  isAnalyzing?: boolean;
  showNoNewDuplicatesMessage?: boolean;
}

interface SimilarityGroup {
  identical: DoclistTreeItemSectionsItem[][];
  verySimilar: DoclistTreeItemSectionsItem[][];
}

export const TextAnalysisTab: FC<TextAnalysisTabProps> = ({
  sections,
  onDeleteClick,
  className = "",
  onSelectGroup,
  selectedGroup,
  aiDetectedGroups = [],
  isAnalyzing = false,
  showNoNewDuplicatesMessage = false,
}) => {
  const intl = useIntl();
  const [similarityGroups, setSimilarityGroups] = useState<SimilarityGroup>({
    identical: [],
    verySimilar: [],
  });

  useEffect(() => {
    const detect = () => {
      const groups = detectSimilarSectionGroups(sections);
      setSimilarityGroups(groups);
    };
    detect();
  }, [sections]);

  const handleDeleteClick = (section: DoclistTreeItemSectionsItem) => {
    trackEvent({ eventName: "app/doc_list/duplicates_delete.button/click" });
    onDeleteClick([section]);
  };

  const hasAnyDuplicates =
    similarityGroups.identical.length > 0 ||
    similarityGroups.verySimilar.length > 0;

  const renderSectionGroup = (
    group: DoclistTreeItemSectionsItem[],
    groupIndex: number
  ) => (
    <div key={groupIndex} className="space-y-1">
      {group.map((section) => (
        <div
          key={section.id}
          className="flex items-center justify-between p-1.5 bg-muted rounded-md"
        >
          <div className="flex-1 min-w-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="font-medium text-xs truncate">
                  {section.name}
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" align="start">
                <div className="max-w-[300px] wrap-break-word text-xs">
                  {section.name}
                </div>
              </TooltipContent>
            </Tooltip>
            <div className="text-xs text-muted-foreground">
              <FormattedMessage
                id="docList.duplicates.pageCount"
                defaultMessage="{count} {count, plural, one {page} other {pages}}"
                values={{ count: section.pageCount }}
              />
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(section);
            }}
            className="ml-2 h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
            aria-label={intl.formatMessage({
              id: "delete",
              defaultMessage: "Delete",
            })}
            title={intl.formatMessage({
              id: "delete",
              defaultMessage: "Delete",
            })}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );

  if (!hasAnyDuplicates) {
    return (
      <div className="text-sm text-muted-foreground">
        <FormattedMessage
          id="docList.duplicates.noneText"
          defaultMessage="No duplicates found by text analysis."
        />
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {(aiDetectedGroups.length > 0 || showNoNewDuplicatesMessage) && (
        <div className="mb-4">
          <h3 className="text-xs font-semibold mb-2 text-purple-700 flex items-center gap-1">
            <FormattedMessage
              id="docList.duplicates.aiDetected"
              defaultMessage="AI Detected"
            />
            {isAnalyzing && (
              <span className="inline-block h-2 w-2 rounded-full bg-purple-700 animate-pulse" />
            )}
          </h3>
          {showNoNewDuplicatesMessage && aiDetectedGroups.length === 0 ? (
            <div className="p-3 bg-muted/30 rounded-md border border-muted animate-in fade-in slide-in-from-left-2">
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="docList.duplicates.noNewDuplicates"
                  defaultMessage="No new duplicate groups found by AI analysis."
                />
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {aiDetectedGroups.map(
                (group: DoclistTreeItemSectionsItem[], index: number) => (
                  <div
                    key={index}
                    className={cn(
                      "rounded-md cursor-pointer transition-all duration-300 animate-in fade-in slide-in-from-left-2",
                      selectedGroup === group
                        ? "border-2 border-blue-500 bg-background"
                        : "border-2 border-transparent hover:bg-muted/50"
                    )}
                    onClick={() => onSelectGroup?.(group)}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {renderSectionGroup(group, index)}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}

      {similarityGroups.identical.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-semibold mb-2 text-red-700">
            <FormattedMessage
              id="docList.duplicates.identical"
              defaultMessage="Identical"
            />
          </h3>
          <div className="space-y-2">
            {similarityGroups.identical.map(
              (group: DoclistTreeItemSectionsItem[], index: number) => (
                <div
                  key={index}
                  className={cn(
                    "rounded-md cursor-pointer transition-colors",
                    selectedGroup === group
                      ? "border-2 border-blue-500 bg-background"
                      : "border-2 border-transparent hover:bg-muted/50"
                  )}
                  onClick={() => onSelectGroup?.(group)}
                >
                  {renderSectionGroup(group, index)}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {similarityGroups.verySimilar.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold mb-2 text-orange-600">
            <FormattedMessage
              id="docList.duplicates.verySimilar"
              defaultMessage="Possible match"
            />
          </h3>
          <div className="space-y-2">
            {similarityGroups.verySimilar.map(
              (group: DoclistTreeItemSectionsItem[], index: number) => (
                <div
                  key={index}
                  className={cn(
                    "rounded-md cursor-pointer transition-colors",
                    selectedGroup === group
                      ? "border-2 border-blue-500 bg-background"
                      : "border-2 border-transparent hover:bg-muted/50"
                  )}
                  onClick={() => onSelectGroup?.(group)}
                >
                  {renderSectionGroup(group, index)}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TextAnalysisTab;
